/**
 * The pure lifecycle of `/fly`'s sprite effects: the animated bursts
 * (`EXPLO.TBL`, and `FLAK.TBL` when there is ever anything to fire it) and the
 * smoke plume a wreck leaves behind. No three.js, so every rule here is a
 * plain-arithmetic unit test — the same split `smoke-trail.ts` uses for the
 * damage trail, and the pools are fixed and recycled for the same reason.
 *
 * ## What a "state" is
 *
 * docs/formats/models-tbl.md §4.3/§4.5: a sprite object's group holds several
 * nodes and the engine draws `stateArray[group.stateChannel]`. For the effect
 * objects that channel is an **animation cursor**, not a damage variant —
 * `EXPLO#0` and `FLAK#0` each carry four nodes whose frames grow monotonically
 * down the states, i.e. the fireball expanding. So playing an explosion is
 * walking `stateIndex` 0 → `stateCount - 1` and then freeing the slot; which
 * *frame* that lands on is a property of the object's own data and is read
 * back out of the bake (`three/sprite-screen-size.ts`'s `billboardForState`).
 *
 * `SMOKE.TBL` is deliberately **not** one of these: it has a single state and
 * twelve LODs, which is a distance ladder for one puff, not an animation
 * (§4.3's "what the LOD ladder means for a sprite object" correction). Smoke
 * is therefore driven by `smoke-trail.ts`'s puff pool and only *drawn* through
 * a sprite; the plume list below exists to keep a wreck emitting into that pool
 * after its aeroplane has stopped being a live smoke source.
 */

import { BIOS_TICK_HZ } from "../audio/engine-voice.ts";
import type { SmokeSource } from "./smoke-trail.ts";

/** Kinds of animated burst. Both have spawners: wrecks and archie (AD-010). */
export const BURST_KINDS = ["explosion", "flakBurst"] as const;

export type BurstKind = (typeof BURST_KINDS)[number];

/**
 * Effect timings the data does not supply, as a register rather than as
 * comments (the 2026-08-19 flight-core lesson: no `TODO` markers, an
 * enumerable work queue instead). §12's group walk shows the engine *reading*
 * `stateArray[stateChannel]`; nothing in the decode shows what advances it, so
 * both numbers below are CALIBRATION-PENDING and both are expressed in the
 * guest's own 18.2065 Hz BIOS tick so a measured tick count drops straight in.
 */
export const EFFECT_TUNABLES = {
  /** Ticks one animation state is held. 3 ticks × 4 states = 0.66 s for a fireball. CALIBRATION-PENDING. */
  burstTicksPerState: 3,
  /** How long a wreck keeps feeding the smoke pool after it is destroyed. CALIBRATION-PENDING. */
  plumeSeconds: 6,
} as const;

export type EffectTunableKey = keyof typeof EFFECT_TUNABLES;

/** Every `EFFECT_TUNABLES` key whose value is a guess rather than a measurement — currently all of them. */
export const EFFECT_CALIBRATION_PENDING: readonly EffectTunableKey[] = ["burstTicksPerState", "plumeSeconds"];

/** Seconds one animation state is held on screen. */
export const BURST_STATE_SECONDS = EFFECT_TUNABLES.burstTicksPerState / BIOS_TICK_HZ;

/**
 * How near the lens an effect sprite may be anchored and still be drawn.
 *
 * The original blits a sprite frame at **1:1 pixels with no perspective
 * scaling** (docs/formats/models-tbl.md §4.3) — distance is expressed only by
 * which rung of the LOD ladder it lands on, and every ladder tops out. So a
 * sprite anchored at arm's length is not a big sprite: it is the *same* sprite
 * an explosion 70 m away draws, pasted over the middle of the view. `EXPLO`'s
 * top rung is 88×32 of a 200-line frame and `SMOKE`'s is 56×49 — a sixth and a
 * quarter of the screen's height, at any range inside 74 m and 8 m respectively.
 *
 * That only bites for the player's own aeroplane, because the cockpit camera
 * sits *inside* it: `cockpit-view.ts`'s crew-sphere offsets put the pilot's eye
 * 0.65-1.85 m ahead of the pose origin that every one of the port's own-aircraft
 * effects is anchored at. Left to the projection alone the outcome is decided by
 * the camera's 1 m near plane (`flight-view.ts`'s `CAMERA_NEAR_M`) — measured,
 * in the rear glance 15 of the 22 flyable scouts clear it and paste that sprite
 * across the view while the other 7 draw nothing at all, and in the `down`
 * glance the anchor sits within ±0.6 m of the lens on every one of them, so it
 * flicks in and out with attitude.
 *
 * A euclidean radius replaces that with one rule that cannot depend on where the
 * pilot is looking. Its value is **the largest flyable airframe's own baked
 * bounding radius** (`HALBD2`, 5.66 m) rounded up: "outside the aeroplane you
 * are sitting in", which is the call `scene-camera.ts`'s `ownAircraftVisible`
 * already makes for the mesh — the port hides the player's own airframe in the
 * cockpit because the `.TBL` LODs have no modelled interior, and a flame or a
 * puff anchored on that airframe is part of the same thing being hidden. What
 * the original puts on screen in its place is not a world sprite either: it is
 * the 2D being-hit overlay its cockpit `.TTM` scripts carry in blocks 28-35
 * (docs/game/ui-menus.md), which is a separate piece of work.
 *
 * It is a *rendering* cull only. A flak burst inside the radius still does its
 * damage, still sounds, and still puts `You've been hit by flak!` on screen.
 */
export const LENS_CLEARANCE_M = 6;

/**
 * Whether an effect anchored at `(ax, ay, az)` is far enough from a lens at
 * `(cx, cy, cz)` to be drawn. Squared on both sides — this runs once per live
 * sprite per frame and the square root would buy nothing.
 */
export function clearsLens(
  ax: number,
  ay: number,
  az: number,
  cx: number,
  cy: number,
  cz: number,
  clearanceM: number = LENS_CLEARANCE_M,
): boolean {
  const dx = ax - cx;
  const dy = ay - cy;
  const dz = az - cz;
  return dx * dx + dy * dy + dz * dz >= clearanceM * clearanceM;
}

/**
 * Pool sizes. A dogfight puts at most a handful of aircraft in the sky and a
 * balloon site a handful of gasbags, so these are generous rather than tuned;
 * they exist to bound the instanced buffers, not to ration anything.
 */
export const MAX_BURSTS = 24;
export const MAX_PLUMES = 8;

/** One animated burst. `age >= life` means the slot is free — the same free-list convention as `smoke-trail.ts`'s puffs. */
export interface Burst {
  kind: BurstKind;
  x: number;
  y: number;
  z: number;
  age: number;
  /** Seconds this burst runs for: its object's own state count times `BURST_STATE_SECONDS`. */
  life: number;
}

/** A wreck still smoking. Feeds `smoke-trail.ts` rather than drawing anything itself. */
export interface Plume {
  x: number;
  y: number;
  z: number;
  age: number;
}

export interface EffectPool {
  readonly bursts: Burst[];
  readonly plumes: Plume[];
}

export function createEffectPool(): EffectPool {
  return {
    bursts: Array.from({ length: MAX_BURSTS }, () => ({ kind: "explosion" as BurstKind, x: 0, y: 0, z: 0, age: 0, life: 0 })),
    plumes: Array.from({ length: MAX_PLUMES }, () => ({ x: 0, y: 0, z: 0, age: EFFECT_TUNABLES.plumeSeconds })),
  };
}

/** A burst slot is live while it still has a state left to show. */
export function burstIsLive(burst: Burst): boolean {
  return burst.age < burst.life;
}

/**
 * Which animation state a burst is showing, or `-1` once it has run out. The
 * cursor is floored, not interpolated: the original swaps a whole sprite frame
 * on a tick boundary and never blends two.
 */
export function burstStateIndex(burst: Burst): number {
  if (!burstIsLive(burst)) return -1;
  return Math.floor(burst.age / BURST_STATE_SECONDS);
}

function freeBurst(pool: EffectPool): Burst | undefined {
  let oldest: Burst | undefined;
  for (const burst of pool.bursts) {
    if (!burstIsLive(burst)) return burst;
    if (!oldest || burst.age > oldest.age) oldest = burst;
  }
  return oldest;
}

/**
 * Starts a burst of `kind` at a world point. `stateCount` is the object's own
 * number of animation states (`stateCountOf` over its baked LODs), so an
 * object with a longer sequence plays for longer without a second constant.
 * Recycles the oldest slot when the pool is full, which is the right choice
 * for an effect: the newest event is the one the player is looking at.
 */
export function spawnBurst(pool: EffectPool, kind: BurstKind, x: number, y: number, z: number, stateCount: number): Burst | undefined {
  if (stateCount <= 0) return undefined;
  const slot = freeBurst(pool);
  if (!slot) return undefined;
  slot.kind = kind;
  slot.x = x;
  slot.y = y;
  slot.z = z;
  slot.age = 0;
  slot.life = stateCount * BURST_STATE_SECONDS;
  return slot;
}

function freePlume(pool: EffectPool): Plume | undefined {
  let oldest: Plume | undefined;
  for (const plume of pool.plumes) {
    if (plume.age >= EFFECT_TUNABLES.plumeSeconds) return plume;
    if (!oldest || plume.age > oldest.age) oldest = plume;
  }
  return oldest;
}

/** Starts a wreck's smoke plume at a world point. */
export function spawnPlume(pool: EffectPool, x: number, y: number, z: number): Plume | undefined {
  const slot = freePlume(pool);
  if (!slot) return undefined;
  slot.x = x;
  slot.y = y;
  slot.z = z;
  slot.age = 0;
  return slot;
}

/** Ages every live slot. Mutates in place: this runs every frame and must not allocate. */
export function advanceEffects(pool: EffectPool, dt: number): void {
  for (const burst of pool.bursts) {
    if (burstIsLive(burst)) burst.age += dt;
  }
  for (const plume of pool.plumes) {
    if (plume.age < EFFECT_TUNABLES.plumeSeconds) plume.age += dt;
  }
}

/**
 * Appends every live plume to `into` as a burning smoke source, so a wreck
 * keeps trailing through the *same* pool a hurt aeroplane does rather than
 * through a second, parallel smoke system.
 */
export function appendPlumeSources(pool: EffectPool, into: SmokeSource[]): void {
  for (const plume of pool.plumes) {
    if (plume.age >= EFFECT_TUNABLES.plumeSeconds) continue;
    into.push({ x: plume.x, y: plume.y, z: plume.z, damageFraction: 1, burning: true });
  }
}

/** How many bursts are currently showing — what a draw-call count and the tests read. */
export function liveBurstCount(pool: EffectPool): number {
  return pool.bursts.filter(burstIsLive).length;
}
